import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { DANGER, IDLE, PENDING } from "const"
import {
    RrsCategory,
    RrsTemplateLabel,
    RrsTemplateRiskRank,
    RrsTemplateRiskRankDelete,
} from "server/models"
import {
    StoreType,
    RrsTemplateRiskRankList,

    CreateRrsTemplateRiskRanks,
    DeleteRrsTemplateRiskRanks,
    LoadAllRrsTemplateRiskRanks,
    SetRrsTemplateRiskMapGenerated,
    SortRrsCategories,

    setRrsTemplateRiskRankList,
    setRrsTemplateEdit,
    initRrsTemplateRiskRank,
    addModal,
} from "client/store"
import { StateType } from "client/utils"
import {
    Container,
    Collapse,
    Loader,
    PageForm,
    Feedback,
} from "client/components"
import {
    RiskMapTable,
    RiskMapTableCell,
} from "./styled"

import RiskRankEdit from "./edit"

type ErrorType = {
    key: string
    error: string
}

interface RiskMapProps {
    templateId: number // too many listeners, templateId required to limit actions
    editAccess: boolean
    showCollapse?: boolean
}
const RiskMap = ({
    templateId,
    editAccess,
    showCollapse = true,
}: RiskMapProps) => {
    const templateEdit = useSelector((state: StoreType) => state.rrsTemplate.templateEdit)
    const labelList = useSelector((state: StoreType) => state.rrsTemplate.labelList)
    const labelCategoryList = useSelector((state: StoreType) => state.rrsTemplate.labelCategoryList)
    const labelLevelList = useSelector((state: StoreType) => state.rrsTemplate.levelList)
    const riskRankList = useSelector((state: StoreType) => state.rrsTemplate.riskRankList)
    const categoryList = useSelector((state: StoreType) => state.rrsCategory.categoryList)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [show, setShow] = useState(true)
    const [errorArray, setErrorArray] = useState<ErrorType[]>([])
    const [riskMapTables, setRiskMapTables] = useState<any[]>([])
    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => {
        if (templateEdit.id !== templateId) return
        if (templateEdit.riskMapGenerated) return

        const errors: ErrorType[] = []

        // Number of labels for matrix - 2. TODO: for graph - unlimited.
        if (labelList.length !== 2) {
            errors.push({ // TODO: update text for graph
                key: "templateMinNumberOfLabels",
                error: lang.templateMinNumberOfLabels,
            })
        }

        // In the risk matrix, we will always have Likelihood and Consequence (or Severity) labels.
        // Only Severity/Consequence has a category and nested categories.
        // For labels with nested categories, the number of levels is the same in each category.
        const labelsWithCategory = labelList.filter((label) => label.categoryId !== null)
        if (labelsWithCategory.length !== 1) {
            errors.push({
                key: "templateNumberOfLabelsWithCategory",
                error: lang.templateNumberOfLabelsWithCategory,
            })
        }

        labelList.forEach((label) => {
            const labelCategories = labelCategoryList[label.id]
            const labelLevels = labelLevelList[label.id] || {} // levels by category
            if (label.categoryId) {
                if (!labelCategories || !labelCategories.length) {
                    errors.push({
                        key: `label_${label.id}_templateLabelNestedCategoriesRequired`,
                        error: lang.templateLabelNestedCategoriesRequired.replace("{0}", label.name),
                    })
                } else {
                    let categoryLevelNumber = -1
                    let categoryLevelNumberCheck = true
                    labelCategories.forEach((categoryId) => {
                        const category = categoryList[categoryId]
                        const categoryLevels = labelLevels[categoryId]
                        if (!categoryLevels || !categoryLevels.levelIds.length) {
                            const error = lang.templateLabelNestedCategoryLevelsRequired
                            .replace("{0}", category ? category.name : "")
                            .replace("{1}", label.name)
                            errors.push({
                                key: `label_${label.id}_category_${categoryId}_templateLabelNestedCategoryLevelsRequired`,
                                error,
                            })
                        } else {
                            const { levelIds } = categoryLevels
                            if (categoryLevelNumber > 0 && levelIds.length !== categoryLevelNumber) categoryLevelNumberCheck = false
                            else categoryLevelNumber = levelIds.length
                        }
                    })
                    if (!categoryLevelNumberCheck) {
                        errors.push({
                            key: `label_${label.id}_templateLabelNestedCategoriesLevelNumber`,
                            error: lang.templateLabelNestedCategoriesLevelNumber.replace("{0}", label.name),
                        })
                    }
                }
            } else {
                const levelsNoCategory = labelLevels[0] // 0 - levels without category for Likelihood label
                if (!levelsNoCategory || !levelsNoCategory.levelIds.length) {
                    errors.push({
                        key: `label_${label.id}_templateLabelLevelsRequired`,
                        error: lang.templateLabelLevelsRequired.replace("{0}", label.name),
                    })
                }
            }
        })

        setErrorArray(errors)
        if (errors.length) return

        let riskRank: RrsTemplateRiskRankList
        if (templateEdit.singleRiskMap) {
            riskRank = singleRiskMap().riskRankListGenerated
        }
        if (!templateEdit.singleRiskMap) {
            riskRank = multipleRiskMap().riskRankListGenerated
        }

        const riskRankCreateData: RrsTemplateRiskRank[] = Object.values(riskRank)
        const riskRankDeleteData: RrsTemplateRiskRankDelete = {
            notInDeleteIds: Object.keys(riskRank),
            templateId: templateEdit.id,
        }

        setLoadState(PENDING)
        Promise.all([
            CreateRrsTemplateRiskRanks(riskRankCreateData),
            DeleteRrsTemplateRiskRanks(riskRankDeleteData),
        ]).then(() => {
            LoadAllRrsTemplateRiskRanks(templateEdit.id).then((res) => {
                dispatch(setRrsTemplateRiskRankList(res))
                SetRrsTemplateRiskMapGenerated([templateEdit.id], true).then(() => {
                    setLoadState(IDLE)
                    dispatch(setRrsTemplateEdit({
                        ...templateEdit,
                        riskMapGenerated: true,
                    }))
                })
            })
        })
    }, [templateEdit, labelList, labelCategoryList, labelLevelList, categoryList])

    useEffect(() => {
        if (templateEdit.id !== templateId) return
        if (!templateEdit.riskMapGenerated) return

        if (templateEdit.singleRiskMap) {
            setRiskMapTables(singleRiskMap().tables)
        }
        if (!templateEdit.singleRiskMap) {
            setRiskMapTables(multipleRiskMap().tables)
        }
    }, [templateEdit, labelList, labelCategoryList, labelLevelList, riskRankList, categoryList])

    const singleRiskMap = () => {
        const riskRankListGenerated: RrsTemplateRiskRankList = {}

        const tables: any[] = []
        const labelRow: any[] = []
        const categoryRow: any[] = []
        const levelRowArray: any[][] = []

        let labelWithCategory: RrsTemplateLabel
        let labelNoCategory: RrsTemplateLabel
        labelList.forEach((label) => {
            if (label.categoryId) labelWithCategory = label
            else labelNoCategory = label
        })

        if (!labelWithCategory || !labelNoCategory) {
            return {
                tables,
                riskRankListGenerated,
            }
        }

        // first label with categories
        const labelListSorted = [labelWithCategory, labelNoCategory]

        labelListSorted.forEach((label) => {
            const labelLevels = labelLevelList[label.id] // levels by category
            if (!labelLevels) return

            let colSpan: number
            if (label.categoryId) {
                const labelCategories = labelCategoryList[label.id]
                if (!labelCategories) return

                colSpan = labelCategories.length

                const categories: RrsCategory[] = []
                labelCategories.forEach((categoryId) => {
                    const category = categoryList[categoryId]
                    if (category) categories.push(category)
                })

                const sortedCategories = SortRrsCategories(categories, {})
                sortedCategories.forEach((category) => {
                    categoryRow.push(
                        <RiskMapTableCell
                            key={`category_${category.id}`}
                            headerCell
                        >
                            {category.shortName.length ? category.shortName : category.name}
                        </RiskMapTableCell>,
                    )

                    if (labelLevels[category.id]) {
                        const { levelIds, levelList } = labelLevels[category.id]
                        levelIds.forEach((levelId, x) => {
                            const level = levelList[levelId]
                            if (!levelRowArray[x]) levelRowArray[x] = []
                            levelRowArray[x].push(
                                <RiskMapTableCell
                                    key={`level_${level.id}`}
                                    headerCell
                                >
                                    {level.name}
                                </RiskMapTableCell>,
                            )
                        })
                    }
                })
            }

            if (!label.categoryId) {
                if (labelLevels[0]) {
                    const { levelIds, levelList } = labelLevels[0]
                    colSpan = levelIds.length

                    levelIds.forEach((levelId) => {
                        const level = levelList[levelId]
                        categoryRow.push(
                            <RiskMapTableCell
                                key={`level_${level.id}`}
                                headerCell
                                centerAlign
                            >
                                {level.name}
                            </RiskMapTableCell>,
                        )
                    })

                    for (let x = 0; x < levelRowArray.length; x++) { // label_1_level_X
                        for (let y = 0; y < levelIds.length; y++) { // label_2_level_Y
                            const riskRankId = `label_1_level_${x}_label_2_level_${y}`
                            const riskRank = riskRankList[riskRankId] || {
                                ...initRrsTemplateRiskRank,
                                textId: riskRankId,
                                templateId: templateEdit.id,
                            }
                            riskRankListGenerated[riskRankId] = riskRank
                            levelRowArray[x].push(
                                <RiskMapTableCell
                                    key={riskRankId}
                                    centerAlign
                                    background={riskRank.color}
                                    onClick={() => {
                                        if (editAccess) {
                                            dispatch(addModal({
                                                key: "editRiskRank",
                                                content: <RiskRankEdit riskRank={riskRank} />,
                                            }))
                                        }
                                    }}
                                >
                                    {`${riskRank.name} (${riskRank.shortName})`}
                                </RiskMapTableCell>,
                            )
                        }
                    }
                }
            }

            labelRow.push(
                <RiskMapTableCell
                    key={`label_${label.id}`}
                    colSpan={colSpan}
                    headerCell
                    centerAlign
                >
                    {`${label.name} (${label.shortName})`}
                </RiskMapTableCell>,
            )
        })

        tables.push(
            <RiskMapTable key="singleRiskMap">
                <tbody>
                    <tr>{labelRow}</tr>
                    <tr>{categoryRow}</tr>
                    {
                        levelRowArray.map((row, index) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <tr key={index}>{row}</tr>
                        ))
                    }
                </tbody>
            </RiskMapTable>,
        )

        return {
            tables,
            riskRankListGenerated,
        }
    }

    const multipleRiskMap = () => {
        const riskRankListGenerated: RrsTemplateRiskRankList = {}
        const tables: any[] = []

        let labelWithCategory: RrsTemplateLabel
        let labelNoCategory: RrsTemplateLabel
        labelList.forEach((label) => {
            if (label.categoryId) labelWithCategory = label
            else labelNoCategory = label
        })

        if (!labelWithCategory || !labelNoCategory) {
            return {
                tables,
                riskRankListGenerated,
            }
        }

        // first label with categories
        const labelListSorted = [labelWithCategory, labelNoCategory]
        const labelCategories = labelCategoryList[labelWithCategory.id]

        if (!labelCategories) {
            return {
                tables,
                riskRankListGenerated,
            }
        }

        const categories: RrsCategory[] = []
        labelCategories.forEach((categoryId) => {
            const category = categoryList[categoryId]
            if (category) categories.push(category)
        })

        const sortedCategories = SortRrsCategories(categories, {})
        sortedCategories.forEach((category) => {
            const labelRow: any[] = []
            const categoryRow: any[] = []
            const levelRowArray: any[][] = []

            labelListSorted.forEach((label) => {
                const labelLevels = labelLevelList[label.id] // levels by category
                if (!labelLevels) return

                let colSpan: number
                if (label.categoryId) {
                    colSpan = 1

                    categoryRow.push(
                        <RiskMapTableCell
                            key={`category_${category.id}`}
                            headerCell
                        >
                            {category.shortName.length ? category.shortName : category.name}
                        </RiskMapTableCell>,
                    )

                    if (labelLevels[category.id]) {
                        const { levelIds, levelList } = labelLevels[category.id]
                        levelIds.forEach((levelId, x) => {
                            const level = levelList[levelId]
                            if (!levelRowArray[x]) levelRowArray[x] = []
                            levelRowArray[x].push(
                                <RiskMapTableCell
                                    key={`level_${level.id}`}
                                    headerCell
                                >
                                    {level.name}
                                </RiskMapTableCell>,
                            )
                        })
                    }
                }

                if (!label.categoryId) {
                    if (labelLevels[0]) {
                        const { levelIds, levelList } = labelLevels[0]
                        colSpan = levelIds.length

                        levelIds.forEach((levelId) => {
                            const level = levelList[levelId]
                            categoryRow.push(
                                <RiskMapTableCell
                                    key={`level_${level.id}`}
                                    headerCell
                                    centerAlign
                                >
                                    {level.name}
                                </RiskMapTableCell>,
                            )
                        })

                        for (let x = 0; x < levelRowArray.length; x++) { // label_1_level_X
                            for (let y = 0; y < levelIds.length; y++) { // label_2_level_Y
                                const riskRankId = `label_1_level_${x}_label_2_level_${y}_category_${category.id}`
                                const riskRank = riskRankList[riskRankId] || {
                                    ...initRrsTemplateRiskRank,
                                    textId: riskRankId,
                                    templateId: templateEdit.id,
                                }
                                riskRankListGenerated[riskRankId] = riskRank
                                levelRowArray[x].push(
                                    <RiskMapTableCell
                                        key={riskRankId}
                                        centerAlign
                                        background={riskRank.color}
                                        onClick={() => {
                                            if (editAccess) {
                                                dispatch(addModal({
                                                    key: "editRiskRank",
                                                    content: <RiskRankEdit riskRank={riskRank} />,
                                                }))
                                            }
                                        }}
                                    >
                                        <span>{`${riskRank.name} (${riskRank.shortName})`}</span>
                                    </RiskMapTableCell>,
                                )
                            }
                        }
                    }
                }

                labelRow.push(
                    <RiskMapTableCell
                        key={`label_${label.id}`}
                        colSpan={colSpan}
                        headerCell
                        centerAlign
                    >
                        {`${label.name} (${label.shortName})`}
                    </RiskMapTableCell>,
                )
            })

            tables.push(
                <RiskMapTable key={`multipleRiskMap_${category.id}`}>
                    <tbody>
                        <tr>{labelRow}</tr>
                        <tr>{categoryRow}</tr>
                        {
                            levelRowArray.map((row, index) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <tr key={index}>{row}</tr>
                            ))
                        }
                    </tbody>
                </RiskMapTable>,
            )
        })

        return {
            tables,
            riskRankListGenerated,
        }
    }

    if (!showCollapse) {
        return (
            <Container>
                {loadState === PENDING && <Loader />}
                {
                    errorArray.length > 0 && (
                        <PageForm>
                            {
                                errorArray.map((row) => (
                                    <Feedback
                                        key={row.key}
                                        variant={DANGER}
                                        transform="none"
                                    >
                                        {row.error}
                                    </Feedback>
                                ))
                            }
                        </PageForm>
                    )
                }
                {
                    !errorArray.length
                    && riskMapTables
                }
            </Container>
        )
    }

    return (
        <Collapse
            show={show}
            onCollapse={() => setShow(!show)}
            title={lang.riskMap}
        >
            {loadState === PENDING && <Loader />}
            {
                errorArray.length > 0 && (
                    <PageForm>
                        {
                            errorArray.map((row) => (
                                <Feedback
                                    key={row.key}
                                    variant={DANGER}
                                    transform="none"
                                >
                                    {row.error}
                                </Feedback>
                            ))
                        }
                    </PageForm>
                )
            }
            {
                !errorArray.length
                && riskMapTables
            }
        </Collapse>
    )
}
export default RiskMap
